import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { generatePrefixedStaticIDs } from 'utils';
import { OrderPropTypes, OrderItemPropTypes } from 'utils/PropTypesValues';
import { statusCodesMap } from 'constants/index';
import { Box, ListItem, Grid, Typography, Divider } from '@material-ui/core';
import { Button, List, OrderHistoryCard } from 'sharedComponents';
import {
  parseOrderDetails,
  parseShippingData,
  parseIfFailedPickupOrder,
  getOrderPrices,
  validateShippingAddress,
  isInStoreOrder as testForInStoreOrder,
  isCmosOrder as testForCmosOrder,
  isConnectOrder as testForConnectOrder,
} from './utils';
import ShippingAddress from '../ShippingAddress';
import Product from '../Product';
import OrderReceipt from '../OrderReceipt';

// According to AC2 of https://neimanmarcus.atlassian.net/browse/OSP-10088
// Connect orders will have terminal number from 500 to 599
const STORE_500_ORDER = /.+:5\d{2}:.+/;
// Determine if the orderId is in-store
const INSTORE_REGEX = /^((\d+:){3}\d+)$/g;

const OrderDetails = ({
  classes,
  order,
  filteredItems,
  groupedServicesList,
  userBrand,
  isFailedOrder,
  isOrderRungToday,
  isVoidForOrderDetailsEnabled,
  openVoidConfirm,
  openInventoryDrawer,
  saveScrollPosition,
  isUserAGeneralManager,
  userStoreNumber,
  selectedStoreId,
  isSendsTakesOrder,
  isGiftReceiptEnabled,
  isSplitGiftReceiptEnabled,
}) => {
  const { orderDetails, paymentDetails, additionalPaymentDetails, cmdUri, assocRingingPin, vendorId } = order;
  const { shippingAddress, items, shipmentMethod } = !_isEmpty(orderDetails) ? orderDetails[0] : {};
  const shipmentMethodDisplay = !isFailedOrder ? shipmentMethod : parseIfFailedPickupOrder(items, shipmentMethod);
  const { amountReceived } = paymentDetails || {};
  const orderInfo = parseOrderDetails(order);
  const method = getOrderPrices(order, isFailedOrder);
  const extra = paymentDetails?.maskedCardNumber?.slice(-4);
  const cardType = paymentDetails?.type;
  const idGenerator = generatePrefixedStaticIDs('detailsId');
  const parsedAddress = validateShippingAddress(shippingAddress) && parseShippingData(shippingAddress);
  const isInStoreOrder = testForInStoreOrder(order.channel);
  const isCmosOrder = testForCmosOrder(order.channel);
  const isConnectOrder = testForConnectOrder(order.channel);
  // Specially for voids: https://neimanmarcus.atlassian.net/browse/OSP-9431
  const isStore500Order = STORE_500_ORDER.test(order.orderId);
  const isVoidOrder = order.orderStatus === 'POSTVOID';
  // AC1 store requirement: https://neimanmarcus.atlassian.net/browse/OSP-9415
  const orderStoreNumber = order.orderId.split(':')[0];
  const storeNumber = isUserAGeneralManager ? selectedStoreId : userStoreNumber;
  const isInSameStore = Number(storeNumber) === Number(orderStoreNumber);
  const hasSplitPayments = additionalPaymentDetails?.length;
  const isVoidButtonVisible =
    isVoidForOrderDetailsEnabled && isOrderRungToday && isStore500Order && !isVoidOrder && isInSameStore;
  const isCommonGiftReceipt = isGiftReceiptEnabled && isOrderRungToday;
  const isSendGiftReceiptButtonVisible = isCommonGiftReceipt && !hasSplitPayments;
  const isSplitGiftReceiptButtonVisible = isCommonGiftReceipt && isSplitGiftReceiptEnabled && hasSplitPayments;

  const renderProduct = (product) => {
    const key = idGenerator.next().value;
    const status = isInStoreOrder ? product.transactionType : product.status;
    const isNegative = statusCodesMap[status]?.isNegative;
    const itemskuId = product.itemskuId || product.pimSkuId;
    const isInStoreLink = INSTORE_REGEX.test(order.orderId);

    const orderIds = {};
    // cmos and pos-send order 2-way linking
    if (isCmosOrder && isInStoreLink) {
      orderIds.posOrderNumber = order.orderId;
    } else if (isInStoreOrder || isConnectOrder) {
      orderIds.cmosOrderNumber = order.cmosOrderNumber;
    }

    const extendedProduct = {
      ...product,
      ...orderIds,
      itemskuId,
      isNegative,
    };

    return (
      <ListItem className={classes.productWrapper} key={key}>
        <Product
          inStoreOrder={isInStoreOrder}
          cmosOrder={isCmosOrder}
          product={extendedProduct}
          userBrand={userBrand}
          giftWrapCode={isFailedOrder && product?.giftWrapCode}
          viewOnly
          ordered
          saveScrollPosition={saveScrollPosition}
          orderData={order}
        />
      </ListItem>
    );
  };

  const renderServices = groupedServicesList.map(
    ({ service, price, title, serviceName, sku }, index) =>
      (title || sku?.includes('OSP')) && (
        //To-do update the key once we got more details in serviceItems. Currently providing the same key when there is multiple similar services.
        <Box className={classes.service} key={service.concat(index)} data-testid="order-details-service">
          <Typography variant="body1">{serviceName}</Typography>
          <Typography variant="body1">${price.toFixed(2)}</Typography>
        </Box>
      ),
  );

  return (
    <>
      {isFailedOrder && (
        <Grid className={classes.grid}>
          <Box className={classes.failedText}>
            <Typography>Call #111 for help with this order.</Typography>
            <Typography>The customer has not been charged.</Typography>
          </Box>
        </Grid>
      )}
      <OrderHistoryCard
        orderInfo={orderInfo}
        isPreSale={order.preSaleFlag}
        vip={order.nmvip}
        details
        isNewStyle={isFailedOrder}
        cmdUri={cmdUri}
        assocRingingPin={assocRingingPin}
        saveScrollPosition={saveScrollPosition}
        orderData={order}
      />
      <Grid className={classes.grid}>
        {parsedAddress && (
          <Box className={classes.shipping}>
            {isFailedOrder ? (
              <Typography variant="h4" className={classes.deliveryLine}>
                Deliver To
              </Typography>
            ) : (
              <Typography variant="h4" paragraph>
                Shipping Address
              </Typography>
            )}

            {!isFailedOrder && <Divider />}
            <ShippingAddress address={parsedAddress} classes={isFailedOrder && classes ? classes : {}} />
            {isFailedOrder && shipmentMethod && (
              <Box>
                <Typography variant="h4" className={classes.deliveryLine}>
                  Delivery Method
                </Typography>
                <Typography className={classes.shippingLine}>{shipmentMethodDisplay}</Typography>
              </Box>
            )}
          </Box>
        )}
      </Grid>

      <Grid className={classes.buttonGrid}>
        {isVoidButtonVisible && (
          <Button className={classes.processButton} variant="outlined" label="Process Void" onClick={openVoidConfirm} />
        )}
        {(isSendGiftReceiptButtonVisible || isSplitGiftReceiptButtonVisible) && (
          <Button
            className={classes.processButton}
            variant="outlined"
            label={`${isSplitGiftReceiptButtonVisible ? 'Send A Split Gift Receipt' : 'Send A Gift Receipt'}`}
            onClick={openInventoryDrawer}
          />
        )}
      </Grid>

      <Box>
        <List className={classes.productList}>{filteredItems?.map(renderProduct)}</List>
      </Box>

      {renderServices}
      {isFailedOrder && <Divider />}
      <Box className={cx(classes.payment, isFailedOrder && classes.paymentExp)}>
        <OrderReceipt
          cardType={cardType}
          cardAmountReceived={amountReceived}
          method={method}
          vendorId={vendorId}
          extra={extra}
          additionalPayments={additionalPaymentDetails}
          isNewStyle={isFailedOrder}
          isVoidOrder={isVoidOrder}
          isSendsTakesOrder={isSendsTakesOrder}
        />
      </Box>
    </>
  );
};

OrderDetails.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  order: OrderPropTypes,
  filteredItems: PropTypes.arrayOf(OrderItemPropTypes),
  groupedServicesList: PropTypes.arrayOf(PropTypes.shape({})),
  isFailedOrder: PropTypes.bool,
  isOrderRungToday: PropTypes.bool,
  isVoidForOrderDetailsEnabled: PropTypes.bool,
  openVoidConfirm: PropTypes.func,
  selectedStoreId: PropTypes.string,
  userStoreNumber: PropTypes.string,
  isUserAGeneralManager: PropTypes.bool,
  isSendsTakesOrder: PropTypes.bool,
  isGiftReceiptEnabled: PropTypes.bool,
  isSplitGiftReceiptEnabled: PropTypes.bool,
};

OrderDetails.defaultProps = {
  classes: {},
  order: {},
  filteredItems: [],
  groupedServicesList: [],
  isFailedOrder: false,
  isOrderRungToday: false,
  isVoidForOrderDetailsEnabled: false,
  selectedStoreId: '',
  userStoreNumber: '',
  isUserAGeneralManager: false,
  isSendsTakesOrder: false,
  isGiftReceiptEnabled: false,
  isSplitGiftReceiptEnabled: false,
};

export default OrderDetails;
